// client/src/utils/permissions.js

/**
 * Permission definitions for the Landivo application
 * These should match the permissions configured in Auth0
 */

export const PERMISSIONS = {
    // Admin management permissions
    READ_ADMINS: 'read:users',
    WRITE_ADMINS: 'write:users',
    DELETE_ADMINS: 'delete:users',
    
    // Property management permissions
    READ_PROPERTIES: 'read:properties',
    WRITE_PROPERTIES: 'write:properties',
    STATUS_PROPERTIES: 'status:properties',
    DELETE_PROPERTIES: 'delete:properties',
    
    // Buyer management permissions
    READ_BUYERS: 'read:buyers',
    WRITE_BUYERS: 'write:buyers',
    DELETE_BUYERS: 'delete:buyers',
    
    // Offer management permissions
    READ_OFFERS: 'read:offers',
    WRITE_OFFERS: 'write:offers',
    DELETE_OFFERS: 'delete:offers',
    
    // Qualification management permissions
    READ_QUALIFICATIONS: 'read:qualifications',
    WRITE_QUALIFICATIONS: 'write:qualifications',
    
    // Email list management permissions
    READ_EMAIL_LISTS: 'read:email-list',
    WRITE_EMAIL_LISTS: 'write:email-list',
    DELETE_EMAIL_LISTS: 'delete:email-list',

    // Admin access permission
    ACCESS_ADMIN: 'access:admin',

    // Traffic analytics access permission
    ACCESS_TRAFFIC: 'access:traffic',
    EXPOSSE_SETTINGS: 'expose:settings',
    EMAIL_SETTINGS: 'email:settings',

    // Settings permissions
    ACCESS_SETTINGS: 'access:settings',


    OBSCURE_PROPERTIES: 'obscure:properties',
  };
  
  /**
   * Role to permission mappings
   * These show what permissions are typically assigned to each role
   * This is for reference only - actual permissions come from Auth0
   */
  export const ROLE_PERMISSIONS = {
    Admin: [
      PERMISSIONS.READ_ADMINS,
      PERMISSIONS.WRITE_ADMINS,
      PERMISSIONS.DELETE_ADMINS,
      PERMISSIONS.READ_PROPERTIES,
      PERMISSIONS.WRITE_PROPERTIES,
      PERMISSIONS.DELETE_PROPERTIES,
      PERMISSIONS.READ_BUYERS,
      PERMISSIONS.WRITE_BUYERS,
      PERMISSIONS.DELETE_BUYERS,
      PERMISSIONS.READ_OFFERS,
      PERMISSIONS.WRITE_OFFERS,
      PERMISSIONS.DELETE_OFFERS,
      PERMISSIONS.READ_QUALIFICATIONS,
      PERMISSIONS.WRITE_QUALIFICATIONS,
      PERMISSIONS.READ_EMAIL_LISTS,
      PERMISSIONS.WRITE_EMAIL_LISTS,
      PERMISSIONS.DELETE_EMAIL_LISTS,
      PERMISSIONS.ACCESS_ADMIN,
      PERMISSIONS.ACCESS_TRAFFIC,
      PERMISSIONS.ACCESS_SETTINGS,
      PERMISSIONS.EXPOSSE_SETTINGS,
      PERMISSIONS.EMAIL_SETTINGS
    ],
    
    Agent: [
      PERMISSIONS.READ_PROPERTIES,
      PERMISSIONS.WRITE_PROPERTIES,
      PERMISSIONS.READ_BUYERS,
      PERMISSIONS.READ_OFFERS,
      PERMISSIONS.READ_QUALIFICATIONS,
    ],
    
    Viewer: [
      PERMISSIONS.READ_PROPERTIES,
    ],
  };
  
  /**
   * Check if an identity has a specific permission
   * @param {Array} grantedPermissions - Array of permission strings
   * @param {String} requiredPermission - Permission to check for
   * @returns {Boolean} - True if identity has the permission
   */
  export const hasPermission = (grantedPermissions, requiredPermission) => {
    return grantedPermissions.includes(requiredPermission);
  };
  
  /**
   * Check if an identity has any of the required permissions
   * @param {Array} grantedPermissions - Array of permission strings
   * @param {Array} requiredPermissions - Array of permissions to check for
   * @returns {Boolean} - True if identity has any of the required permissions
   */
  export const hasAnyPermission = (grantedPermissions, requiredPermissions) => {
    return requiredPermissions.some((permission) => grantedPermissions.includes(permission));
  };
  
  /**
   * Check if an identity has all of the required permissions
   * @param {Array} grantedPermissions - Array of permission strings
   * @param {Array} requiredPermissions - Array of permissions to check for
   * @returns {Boolean} - True if identity has all of the required permissions
   */
  export const hasAllPermissions = (grantedPermissions, requiredPermissions) => {
    return requiredPermissions.every((permission) => grantedPermissions.includes(permission));
  };
